import { Tab, Tabs, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { ReactNode, JSX } from 'react';

export type ItemConfig<Item> = Readonly<{
	item: Item;
	title: JSX.Element;
	isActive: () => boolean;
	onNavigate: () => void;
}>;

type Props<Item> = Readonly<{
	variant: 'tabs' | 'buttons';
	config: readonly ItemConfig<Item>[];
}>;

const Switcher = <Item extends string>({ config, variant }: Props<Item>): ReactNode => {
	const theme = useTheme();

	const activeItem = config.find(({ isActive }) => isActive())?.item;

	if (variant === 'tabs') {
		return (
			<Tabs
				value={activeItem}
				onChange={(_, value) => {
					config.find(({ item }) => item === value)?.onNavigate();
				}}
				sx={{ borderBottom: 1, borderColor: 'divider', width: 'fit-content' }}
			>
				{config.map(({ item, title }) => (
					<Tab key={item} label={title} value={item} sx={{ textTransform: 'none' }} />
				))}
			</Tabs>
		);
	}

	return (
		<ToggleButtonGroup sx={{ alignItems: 'stretch' }} value={activeItem} exclusive>
			{config.map(({ item, title, onNavigate }) => (
				<ToggleButton
					key={item}
					sx={{
						color: theme.palette.text.primary,
						outline: `1px solid ${theme.palette.grey[300]}`,
						outlineOffset: -1,
						paddingY: 0.75,
						paddingX: { xs: 1, lg: 2 },
						fontSize: {
							xs: theme.typography.subtitle1.fontSize,
							lg: theme.typography.subtitle1.fontSize,
						},
						lineHeight: 1.5,
						fontWeight: 500,
						transition: 'all 0.2s ease',
						'&.Mui-selected': {
							backgroundColor: theme.palette.grey[800],
							color: theme.palette.grey[0],
							border: 'none',
							outline: 'none',
						},
						'&.Mui-selected:hover': {
							backgroundColor: theme.palette.grey[600],
						},
						'&.Mui-selected.MuiToggleButtonGroup-grouped:not(:first-of-type)': {
							borderColor: 'unset',
						},
					}}
					value={item}
					onClick={onNavigate}
				>
					{title}
				</ToggleButton>
			))}
		</ToggleButtonGroup>
	);
};

export { Switcher };
